import { useState } from "react";
import { toast } from "sonner";
import { Search, Home, LocateFixed, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

export const StartPicker = ({ value, onChange, home, testPrefix = "start" }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (q.trim().length < 3) return;
    setBusy(true);
    try {
      const { data } = await api.get("/geocode", { params: { q } });
      setResults(data);
      if (!data.length) toast.message("No US matches. Try adding a city and state.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const locate = () => {
    if (!navigator.geolocation) return toast.error("Location isn't available in this browser. Type an address instead.");
    navigator.geolocation.getCurrentPosition(
      (pos) => onChange({ label: "Current location (used only for this plan)", lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => toast.error("Couldn't get your location. Type an address instead."),
    );
  };

  const pick = (r) => {
    onChange({ label: r.label.split(",").slice(0, 3).join(","), lat: r.lat, lon: r.lon });
    setResults([]);
    setQ("");
  };

  return (
    <div className="space-y-2">
      {value && (
        <div data-testid={`${testPrefix}-selected`} className="text-sm bg-[#E3EFE6] text-[#0F2A1F] rounded-lg px-3 py-2 font-medium truncate">{value.label}</div>
      )}
      <div className="flex gap-2">
        <input data-testid={`${testPrefix}-input`} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), search())}
          placeholder="Address, neighborhood or city (US)" className="flex-1 min-w-0 h-10 rounded-lg border border-stone-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F7D55]/40" />
        <button type="button" data-testid={`${testPrefix}-search-btn`} onClick={search} className="h-10 px-3 rounded-lg bg-stone-900 text-white hover:bg-stone-700 transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </button>
      </div>
      {results.length > 0 && (
        <ul className="border border-stone-200 rounded-lg bg-white divide-y max-h-48 overflow-auto" data-testid={`${testPrefix}-results`}>
          {results.map((r, i) => (
            <li key={i}><button type="button" data-testid={`${testPrefix}-result-${i}`} onClick={() => pick(r)} className="w-full text-left text-sm px-3 py-2 hover:bg-stone-50">{r.label}</button></li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-2">
        {home && (
          <button type="button" data-testid={`${testPrefix}-home-btn`} onClick={() => onChange(home)} className="text-xs font-semibold flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-stone-300 hover:bg-stone-100">
            <Home className="w-3 h-3" /> Home area
          </button>
        )}
        <button type="button" data-testid={`${testPrefix}-locate-btn`} onClick={locate} className="text-xs font-semibold flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-stone-300 hover:bg-stone-100">
          <LocateFixed className="w-3 h-3" /> Use my location (only when I tap)
        </button>
      </div>
    </div>
  );
};
